import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { useUiStore } from "../store/uiStore";

/**
 * Modal overlay for validation failures. Shows when the user tries to
 * navigate with invalid fields. Primary action (Ignore) lets them continue;
 * secondary (Review Fields) dismisses and returns to the form.
 */
export function ValidationModal() {
  const validationModal = useUiStore((s) => s.validationModal);
  const dismissValidationModal = useUiStore((s) => s.dismissValidationModal);

  // Close on Escape
  useEffect(() => {
    if (!validationModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismissValidationModal();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [validationModal, dismissValidationModal]);

  if (!validationModal) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Validation required"
    >
      {/* Dimmed backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={dismissValidationModal}
      />

      {/* Modal content */}
      <div className="relative bg-white rounded-xl shadow-2xl px-8 py-6 max-w-md w-[90%] section-reveal">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle size={28} className="text-[#F78F4E] shrink-0" />
          <h2 className="text-lg font-semibold text-[#232942]">
            One or more fields need attention!
          </h2>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Some fields on this page are incomplete or contain errors. You can review and fix them, or ignore and continue to the next page.
        </p>

        <div className="flex gap-3 justify-center">
          <button
            onClick={dismissValidationModal}
            className="px-5 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            Review Fields
          </button>
          <button
            onClick={() => {
              validationModal.onIgnore();
              dismissValidationModal();
            }}
            className="px-5 py-2.5 rounded-lg text-sm font-medium bg-[#F78F4E] text-white hover:opacity-90 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-[#F78F4E]/50"
          >
            Ignore & Continue
          </button>
        </div>
      </div>
    </div>
  );
}
